/**
 * appsRail — the four apps, and the two counts that are real.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ FOUR ITEMS, NOT THE DESIGN'S FIVE. CALENDAR IS NOT HERE.                  ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * Surveyed 2026-09-09 before anything was built. **There is no calendar feature.**
 * The only calendar route in the app is `hr/calendar` → `LeaveCalendarPage`, an
 * absence calendar, which is a different product from the one the rail implies.
 * A `company_events` table exists with 6 rows and 104 RSVPs, so the data half is
 * there with no app on top of it.
 *
 * Lis's ruling 2026-09-09: leave Calendar out. A row that opens the wrong screen
 * is worse than a row that is missing.
 *
 * ── ⛔ TWO BADGES, NOT FOUR, AND THE ABSENT TWO ARE THE POINT ─────────────────
 * Measured on production the same day, for the real user:
 *
 *     My tasks   6   open and assigned to her          <- REAL, shown
 *     Chat       2   unread direct messages            <- REAL, shown
 *     Webmail  183   "unread" — but that counts EVERY message including her own
 *                    sent mail. Per-person read state (`mail_message_state`)
 *                    holds EIGHT rows in total, so the number is not a measure
 *                    of anything a person would recognise.        <- NOT shown
 *     Directory  —   nothing to count.                            <- NOT shown
 *
 * The design draws Webmail 50. Nothing in the database produces 50, and a badge
 * that is confidently wrong is worse than no badge: it is the one part of a
 * sidebar people trust without opening.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ THE WEBMAIL BADGE IS SHOWN SINCE 2026-10-02, AND THE RULING ABOVE WAS     ║
 * ║     RIGHT WHEN IT WAS MADE. THE PREMISE MOVED, NOT THE REASONING.            ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * The 2026-09-09 ruling rests on one measurement: `mail_message_state` held **8
 * rows**, so per-mailbox read state described almost nothing and the only number
 * available counted sent mail too. Re-measured on production 2026-10-02, before
 * writing a line of this:
 *
 *                                        2026-09-09      2026-10-02
 *     mail_message_state rows                     8           1,591   (one per live message)
 *     …of those, is_read = false                  —              36   <- THE BADGE
 *     mail_message.is_read = false, inbound     183           1,160   <- STILL WRONG
 *
 * ⛔ **THE GAP BETWEEN THOSE LAST TWO LINES IS THE WHOLE REASON THIS FUNCTION
 * READS THE STATE TABLE.** The obvious column, `mail_message.is_read`, would put
 * **1,160** on the badge — which is exactly the confidently-wrong number the
 * ruling above refused, and it is one join away from the right one. Anybody
 * "simplifying" this query to the message table is re-introducing the defect the
 * old note exists to prevent.
 *
 * ⚠️ AND THE SCOPING IS RLS'S, NOT THIS FILE'S. `mail_message_state_rw` is
 * `workspace_id = current_user_workspace() AND EXISTS (… a.owner_id = auth.uid())`
 * — so the rows this count can see are already only the viewer's OWN mailboxes.
 * A client-side `account_id in (…)` filter would be a second, weaker copy of a
 * rule the database already enforces.
 *
 * ⚠️ It is NOT the triage "Open" count (1,177 on the same day), which the mail
 * rail shows separately. Triage is "have I dealt with this conversation"; the
 * badge answers "is there mail I have not read". Two different questions that
 * happen to live on one screen.
 *
 * ⚠️ **AND THE CHAT COUNT IS DELIBERATELY NARROWER THAN "CHAT".** It counts
 * unread DIRECT MESSAGES only. `messages` (channel posts, 127 rows across 10
 * channels) has NO read-state column at all — there is nothing to compare
 * against — so channel unread is not derivable today. Lis's ruling: Chat links
 * to Direct Messages, and the badge is the DM count. When channels gain read
 * state, this is the function that changes.
 */

import { supabase } from './supabase';

export interface AppsRailCounts {
  /** Open tasks assigned to the viewer. Null while unknown — never guessed. */
  myTasks: number | null;
  /** Unread direct messages TO the viewer. Null while unknown. */
  chat: number | null;
  /** Unread mail in the viewer's OWN mailboxes, from `mail_message_state`. Null while
   *  unknown. See the header for why it is not `mail_message.is_read`. */
  webmail: number | null;
}

export const EMPTY_COUNTS: AppsRailCounts = { myTasks: null, chat: null, webmail: null };

/**
 * The two counts, for the signed-in person.
 *
 * ⚠️ **NULL IS A REAL ANSWER AND MUST NOT BECOME ZERO.** A failed read that
 * renders "0" tells the reader they have nothing waiting, which is a different
 * and worse statement than telling them nothing. Every failure below leaves the
 * value null and the badge simply does not draw.
 */
export async function fetchAppsRailCounts(): Promise<AppsRailCounts> {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const userId = session.session?.user?.id ?? null;
  if (!userId) return EMPTY_COUNTS;

  const counts: AppsRailCounts = { myTasks: null, chat: null, webmail: null };

  /* ── MY TASKS ────────────────────────────────────────────────────────────
     "Open" is `status <> 'done'`, which is the definition `recordTasks.ts`
     already uses (`isTaskDone`). Four statuses exist — todo, in_progress,
     in_review, done — so open is NOT a boolean and re-deriving it here with a
     different rule would make the badge disagree with the page it links to. */
  try {
    const { count, error } = await supabase
      .from('tasks')
      .select('id', { count: 'exact', head: true })
      .eq('assignee_id', userId)
      .eq('is_deleted', false)
      .neq('status', 'done');
    if (error) throw error;
    counts.myTasks = count ?? null;
  } catch {
    /* left null — see the note above */
  }

  /* ── CHAT: unread DIRECT MESSAGES only ───────────────────────────────────
     `direct_messages` carries `read_at`, so unread is `read_at is null` for
     messages addressed TO this person. `to_user_id` is the real column name —
     an earlier draft of this query guessed `recipient_id` and Postgres refused
     it, which is the cheap version of the same mistake. */
  try {
    const { count, error } = await supabase
      .from('direct_messages')
      .select('id', { count: 'exact', head: true })
      .eq('to_user_id', userId)
      .eq('is_deleted', false)
      .is('read_at', null);
    if (error) throw error;
    counts.chat = count ?? null;
  } catch {
    /* left null */
  }

  /* ── WEBMAIL: unread in the viewer's OWN mailboxes ───────────────────────
     ⛔ `mail_message_state`, NEVER `mail_message`. The message table's `is_read`
     counts everything the mailbox ever held, sent mail included — 1,160 against
     this table's 36 on 2026-10-02. The header carries both numbers and the date.

     ⚠️ NO `account_id` FILTER, AND THAT IS NOT AN OVERSIGHT. The RLS policy
     `mail_message_state_rw` already restricts these rows to accounts whose
     `owner_id = auth.uid()`, so the scoping is the database's. Re-stating it here
     would be a second copy of a rule, free to drift from the one that is enforced. */
  try {
    const { count, error } = await supabase
      .from('mail_message_state')
      .select('message_id', { count: 'exact', head: true })
      .eq('is_read', false);
    if (error) throw error;
    counts.webmail = count ?? null;
  } catch {
    /* left null — no badge, rather than a zero that claims an empty inbox */
  }

  return counts;
}
